import React, { useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import styled from "styled-components";
import {
  Card,
  Table,
  Spin,
  Typography,
  Button,
  Switch,
  message,
  Tabs,
  Alert,
  List,
} from "antd";
import {
  ArrowLeftOutlined,
  DollarOutlined,
  FilePdfOutlined,
  CheckCircleOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import {
  getTenantLedger,
  updateTenant,
  getSignedLeaseUrl,
  getTenantTickets,
  confirmUtilities,
} from "../lib/api/tenantLedgerApi";
import Logo from "../components/general/Logo";

const { Title, Text } = Typography;

const PageWrap = styled.div`
  min-height: 100vh;
  background: var(--color-background, #f5f5f5);
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const StyledCard = styled(Card)`
  max-width: 720px;
  width: 100%;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  .ant-card-body {
    padding: 1.5rem;
  }
`;

const LogoWrap = styled.div`
  margin-bottom: 1rem;
  text-align: center;
`;

const SectionTitle = styled(Title)`
  margin-top: 0 !important;
  margin-bottom: 1rem !important;
`;

const AutoPayRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.25rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid #f0f0f0;
`;

const ChecklistCard = styled(Card)`
  margin-bottom: 1rem;
  .ant-card-body {
    padding: 0.75rem 1rem;
  }
`;

const TabContent = styled.div`
  margin-top: 0.5rem;
`;

/**
 * Tenant Ledger: table of rent charges and payments with running balance.
 * Auto-Pay toggle updates tenant.autopayEnabled.
 * Auth: only tenant or landlord can access (enforced by API).
 */
const TenantLedgerView = () => {
  const { tenantId } = useParams();
  const [searchParams] = useSearchParams();
  const urlToken = searchParams.get("token");
  const queryClient = useQueryClient();
  const [autopayEnabled, setAutopayEnabled] = useState(false);
  const [activeTab, setActiveTab] = useState("ledger");

  const { data, isLoading, error } = useQuery({
    queryKey: ["tenant-ledger", tenantId, urlToken || ""],
    queryFn: () => getTenantLedger(tenantId, urlToken || undefined),
    enabled: !!tenantId,
  });

  const { data: signedLease, isLoading: signedLeaseLoading } = useQuery({
    queryKey: ["tenant-signed-lease", tenantId, urlToken || ""],
    queryFn: () => getSignedLeaseUrl(tenantId, urlToken || undefined),
    enabled: !!tenantId && activeTab === "documents",
    retry: false,
  });

  const { data: ticketsData, isLoading: ticketsLoading } = useQuery({
    queryKey: ["tenant-tickets", tenantId, urlToken || ""],
    queryFn: () => getTenantTickets(tenantId, urlToken || undefined),
    enabled: !!tenantId && activeTab === "maintenance",
  });

  const confirmUtilitiesMutation = useMutation({
    mutationFn: () => confirmUtilities(tenantId, urlToken || undefined),
    onSuccess: () => {
      message.success("Landlord notified that you confirmed utilities.");
      queryClient.invalidateQueries({ queryKey: ["tenant-ledger", tenantId] });
    },
    onError: (e) => message.error(e.message || "Failed to confirm"),
  });

  const mutation = useMutation({
    mutationFn: (value) =>
      updateTenant(tenantId, { autopayEnabled: value }, urlToken || undefined),
    onSuccess: (_, value) => {
      setAutopayEnabled(value);
      queryClient.invalidateQueries({ queryKey: ["tenant-ledger", tenantId] });
      message.success(value ? "Auto-pay enabled." : "Auto-pay disabled.");
    },
    onError: (e) => message.error(e.message || "Failed to update auto-pay"),
  });

  const entries = data?.entries || [];
  const repairTickets = ticketsData?.repairTickets || [];
  const openTickets = repairTickets.filter(
    (t) => t.status && !["completed", "closed", "cancelled"].includes(t.status)
  );
  const hasUnpaidSecurityDeposit = entries.some(
    (e) =>
      e.description &&
      e.description.includes("Security Deposit") &&
      e.status !== "paid" &&
      e.status !== "succeeded"
  );

  if (isLoading) {
    return (
      <PageWrap>
        <Spin size="large" tip="Loading ledger…" />
      </PageWrap>
    );
  }

  if (error) {
    return (
      <PageWrap>
        <StyledCard>
          <Title level={5}>Unable to load ledger</Title>
          <Text type="secondary">
            You may not have permission to view this ledger, or the link is
            invalid.
          </Text>
        </StyledCard>
      </PageWrap>
    );
  }

  const currentAutopay = data?.autopayEnabled ?? autopayEnabled;

  const columns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      width: 120,
      render: (d) => d?.slice(0, 10) || "",
    },
    { title: "Description", dataIndex: "description", key: "description" },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      align: "right",
      width: 120,
      render: (amt) => (
        <Text type={amt >= 0 ? "success" : undefined}>
          {amt >= 0 ? "+" : ""}${Number(amt).toFixed(2)}
        </Text>
      ),
    },
    { title: "Status", dataIndex: "status", key: "status", width: 100 },
    {
      title: "Balance",
      dataIndex: "balance",
      key: "balance",
      align: "right",
      width: 120,
      render: (b) => `$${Number(b).toFixed(2)}`,
    },
  ];

  return (
    <PageWrap>
      <StyledCard>
        <LogoWrap>
          <Logo />
        </LogoWrap>
        <SectionTitle level={4}>
          <DollarOutlined style={{ marginRight: 8 }} />
          Your ledger
          {(data?.is_draft || data?.ingestion_status === "applied") && (
            <span
              style={{
                marginLeft: 12,
                fontSize: 12,
                fontWeight: 600,
                color: "#1890ff",
                background: "#e6f7ff",
                padding: "2px 8px",
                borderRadius: 4,
                border: "1px solid #91d5ff",
              }}
            >
              Status: Applied
            </span>
          )}
        </SectionTitle>

        {hasUnpaidSecurityDeposit && (
          <ChecklistCard
            size="small"
            style={{ borderColor: "#faad14", background: "#fffbe6" }}
          >
            <Alert
              type="warning"
              showIcon
              icon={<CheckCircleOutlined />}
              message="Move-in checklist"
              description="Pay your security deposit to complete move-in. You can pay from the Ledger tab once charges appear."
            />
          </ChecklistCard>
        )}

        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: "ledger",
              label: "Ledger",
              children: (
                <TabContent>
                  <AutoPayRow>
                    <Text>Auto-pay rent</Text>
                    <Switch
                      checked={currentAutopay}
                      onChange={(checked) => mutation.mutate(checked)}
                      loading={mutation.isPending}
                    />
                  </AutoPayRow>
                  <Table
                    dataSource={entries.map((e, i) => ({
                      ...e,
                      key: (e.date || "") + i,
                    }))}
                    columns={columns}
                    pagination={entries.length > 10 ? { pageSize: 10 } : false}
                    size="small"
                  />
                </TabContent>
              ),
            },
            {
              key: "documents",
              label: "Documents",
              children: (
                <TabContent>
                  {signedLeaseLoading ? (
                    <Spin tip="Loading…" />
                  ) : signedLease?.url ? (
                    <div>
                      <Button
                        type="primary"
                        icon={<FilePdfOutlined />}
                        href={signedLease.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Download signed lease
                      </Button>
                    </div>
                  ) : (
                    <Text type="secondary">
                      No signed lease on file. Your landlord will add it after
                      you sign.
                    </Text>
                  )}
                  <div style={{ marginTop: 16 }}>
                    <Button
                      type="default"
                      icon={<ThunderboltOutlined />}
                      onClick={() => confirmUtilitiesMutation.mutate()}
                      loading={confirmUtilitiesMutation.isPending}
                    >
                      Confirm utilities transferred
                    </Button>
                    <Text
                      type="secondary"
                      style={{ display: "block", marginTop: 8, fontSize: 12 }}
                    >
                      Notifies your landlord that you have put utilities in your
                      name.
                    </Text>
                  </div>
                </TabContent>
              ),
            },
            {
              key: "maintenance",
              label: "Maintenance",
              children: (
                <TabContent>
                  {ticketsLoading ? (
                    <Spin tip="Loading…" />
                  ) : openTickets.length === 0 ? (
                    <Text type="secondary">No open repair tickets.</Text>
                  ) : (
                    <List
                      size="small"
                      dataSource={openTickets}
                      renderItem={(t) => {
                        let scheduled = null;
                        if (t.scheduledDate) {
                          if (typeof t.scheduledDate === "string")
                            scheduled = new Date(t.scheduledDate);
                          else if (t.scheduledDate?.seconds)
                            scheduled = new Date(
                              t.scheduledDate.seconds * 1000
                            );
                          else scheduled = new Date(t.scheduledDate);
                        }
                        const statusText = scheduled
                          ? `Scheduled for ${scheduled.toLocaleDateString(
                              "en-US",
                              { weekday: "long" }
                            )}`
                          : (t.status || "Open").replace(/_/g, " ");
                        return (
                          <List.Item>
                            <div>
                              <Text strong>{t.title}</Text>
                              <br />
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                {statusText}
                                {t.category &&
                                  ` · ${String(t.category).replace(/_/g, " ")}`}
                              </Text>
                            </div>
                          </List.Item>
                        );
                      }}
                    />
                  )}
                </TabContent>
              ),
            },
          ]}
        />

        <div style={{ marginTop: 16 }}>
          <Button
            type="default"
            icon={<ArrowLeftOutlined />}
            onClick={() => window.history.back()}
          >
            Back
          </Button>
        </div>
      </StyledCard>
    </PageWrap>
  );
};

export default TenantLedgerView;
